import {
  DreamerLineChartDataType,
  LineConfig
} from "@/components/DreamerCharts/types";
import { CHART_SEGMENT_COLORS } from "@/components/DreamerCharts/chartColors";
import {
  DreamerAttachmentColumn,
  DreamerChartAxisLabels,
  DreamerTableRow
} from "@/entities/attachments/types";

interface BuildLineChartDataParams {
  rows: DreamerTableRow[];
  axisLabels: DreamerChartAxisLabels | null;
  valueColumn: DreamerAttachmentColumn | undefined;
  isFractionalPercent: boolean;
  formatLabel: (raw: unknown) => string;
}

interface BuildLineChartDataResult {
  data: DreamerLineChartDataType[];
  lines: LineConfig[];
}

export const buildLineChartData = ({
  rows,
  axisLabels,
  valueColumn,
  isFractionalPercent,
  formatLabel
}: BuildLineChartDataParams): BuildLineChartDataResult => {
  if (!axisLabels?.x || !axisLabels?.y || !valueColumn) {
    return { data: [], lines: [] };
  }

  const xAxisKey = axisLabels.x;
  const yAxisKey = axisLabels.y;
  const legendAxisKey = axisLabels.legend;

  const toNumber = (raw: unknown): number => {
    const numericValue = typeof raw === "number" ? raw : Number(raw);
    const safeNumericValue = Number.isFinite(numericValue) ? numericValue : 0;
    return isFractionalPercent ? safeNumericValue * 100 : safeNumericValue;
  };

  if (!legendAxisKey) {
    const data = rows.map((row) => ({
      label: formatLabel(row[xAxisKey]),
      [yAxisKey]: toNumber(row[yAxisKey])
    }));
    return {
      data,
      lines: [
        {
          dataKey: yAxisKey,
          color: CHART_SEGMENT_COLORS[0],
          label: valueColumn.label
        }
      ]
    };
  }

  const lineKeys: string[] = [];
  const seenLines = new Set<string>();
  const xValueOrder: string[] = [];
  const xValueIndexMap = new Map<string, DreamerLineChartDataType>();

  rows.forEach((row) => {
    // Skip rows missing a legend value rather than collapsing them into a
    // blank-labeled series — that produced an empty legend chip and merged
    // unrelated rows into one line.
    const rawLegendValue = row[legendAxisKey];
    if (rawLegendValue === null || rawLegendValue === undefined) return;
    const lineKey = String(rawLegendValue);
    if (lineKey === "") return;

    if (!seenLines.has(lineKey)) {
      seenLines.add(lineKey);
      lineKeys.push(lineKey);
    }

    const xLabel = formatLabel(row[xAxisKey]);
    let entry = xValueIndexMap.get(xLabel);
    if (!entry) {
      entry = { label: xLabel };
      xValueIndexMap.set(xLabel, entry);
      xValueOrder.push(xLabel);
    }
    entry[lineKey] = toNumber(row[yAxisKey]);
  });

  const lines: LineConfig[] = lineKeys.map((lineKey, index) => ({
    dataKey: lineKey,
    color: CHART_SEGMENT_COLORS[index % CHART_SEGMENT_COLORS.length],
    label: lineKey
  }));

  return {
    data: xValueOrder.map(
      (xLabel) => xValueIndexMap.get(xLabel) as DreamerLineChartDataType
    ),
    lines
  };
};
